import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircularProgress } from "@mui/material";

const CartItemsList = ({
  cartItems = [],
  productImage,
  handleRemoveItemFromCart,
  onIncreaseQuantity,
  onDecreaseQuantity,
}) => {
  const [removingId, setRemovingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const handleRemove = async (item) => {
    const itemId = item?._id || item?.id;
    if (!itemId) return;

    setRemovingId(itemId);
    try {
      await handleRemoveItemFromCart(item);
    } finally {
      setRemovingId(null);
    }
  };

  const handleQuantityChange = async (item, change) => {
    const itemId = item?._id || item?.id;
    if (!itemId) return;

    setUpdatingId(itemId);
    try {
      if (change === 'increase') {
        await onIncreaseQuantity?.(item);
      } else {
        await onDecreaseQuantity?.(item);
      }
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="cart-items-container" role="list" aria-label="Cart items list">
      <AnimatePresence>
        {Array.isArray(cartItems) && cartItems.map((item) => {
          if (!item || !item.product) return null;

          const itemId = item._id || item.id;
          const isRemoving = removingId === itemId;
          const isUpdating = updatingId === itemId;
          const isLoading = isRemoving || isUpdating;

          return (
            <motion.div
              key={itemId}
              className="cart-item"
              layout
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Loading overlay for the entire item */}
              {isUpdating && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}>
                  <CircularProgress size={24} />
                </div>
              )}

              {/* Product Image */}
              <div className="cart-item-img">
                <img
                  src={productImage(item)}
                  alt={item.product.title}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "https://via.placeholder.com/150";
                  }}
                />
              </div>

              {/* Product Details */}
              <div className="cart-item-details">
                <div className="cart-item-name">{item.product.title}</div>
                <div className="quantity-controls">
                  <button
                    disabled={isLoading}
                    onClick={() => handleQuantityChange(item, 'decrease')}
                  >
                    –
                  </button>
                  
                  <div className="quantity-display">
                    {item.quantity}
                  </div>
                  
                  <button
                    disabled={isLoading}
                    onClick={() => handleQuantityChange(item, 'increase')}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Price */}
              <div className="cart-item-price">
                ₹{item.product.discountedPrice * item.quantity}
              </div>

              {/* Remove Button */}
              <button
                className="remove-btn"
                onClick={() => handleRemove(item)}
                disabled={isLoading}
              >
                {isRemoving ? <CircularProgress size={16} /> : "×"}
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {cartItems.length === 0 && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="empty-cart-message"
        >
          Your cart is empty.
        </motion.p>
      )}
    </div>
  );
};

export default CartItemsList;